import { useEffect, useState } from 'react';
import { Organization } from '../generated/graphql';
import { getPathByOrganizationId } from '../utility/getPathByOrganizationId';
import { getUrlByCondition } from '../utility/getUrlByCondition';
import { useNavigate } from 'react-router-dom';
import { setConditionDisplayFromUrl } from '../utility/setConditionDisplayFromUrl';
import { getPathWithQueryAdd } from '../utility/getPathWithQueryAdd';

type CheckboxStates = {
  singlePageTest: boolean; //ページ修正（UIエディター）
  redirectTest: boolean; //ページ修正（スプリットURL）
  replaceImage: boolean; //画像の差し替え
  displayPopup: boolean; //ポップアップ
  notActive: boolean; //テスト準備中
  competition: boolean; //ラウンド実施中
  interim: boolean; //インターバル
  paused: boolean; //ラウンド一時停止中
  archived: boolean; //テスト終了
  editRequest: boolean; //修正反映待ち（仮）
  variation: boolean; //採用待ち（仮）
  advertising: boolean; //デザイン案を募集中
  closed: boolean; //デザイン案募集が終了（締切日超過）
};

type SetConditionStates = {
  setKeyword: (arg0: string) => void;
  setType: (arg0: 'open' | 'targeting' | 'my_team' | 'limited_open' | 'private' | 'all') => void;
  setDevice: (arg0: 'pc' | 'sp' | 'all') => void;
  setTestingExperience: (arg0: 'except' | 'only' | 'all') => void;
  setOrder: (arg0: 'created_at_desc' | 'created_at_asc' | 'cvr_desc' | 'title_asc') => void;
  setCheckboxStates: React.Dispatch<
    React.SetStateAction<{
      singlePageTest: boolean;
      redirectTest: boolean;
      replaceImage: boolean;
      displayPopup: boolean;
      notActive: boolean;
      competition: boolean;
      interim: boolean;
      paused: boolean;
      archived: boolean;
      editRequest: boolean;
      variation: boolean;
      advertising: boolean;
      closed: boolean;
    }>
  >;
};

/**
 *
 * @returns useQuery
 */
export const useQueryString = (
  { keyword, type, device, testingExperience, order }: any,
  checkboxStates: CheckboxStates,
  lastOrg: Organization,
  { setKeyword, setType, setDevice, setTestingExperience, setOrder, setCheckboxStates }: SetConditionStates,
): {
  queryString: string;
  setQueryString: (arg0: string) => void;
  currentPage: number;
  setCurrentPage: (arg0: number) => void;
} => {
  const [queryString, setQueryString] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1); //現在ページ
  const navigate = useNavigate();

  const _setQueryString = ({
    keyword,
    type,
    device,
    testingExperience,
    order,
    singlePageTest,
    redirectTest,
    replaceImage,
    displayPopup,
    notActive,
    competition,
    interim,
    paused,
    archived,
    editRequest,
    variation,
    advertising,
    closed,
  }: {
    keyword: string;
    type: 'open' | 'targeting' | 'my_team' | 'limited_open' | 'private' | 'all';
    device: 'pc' | 'sp' | 'all';
    testingExperience: 'except' | 'only' | 'all';
    order: 'created_at_desc' | 'created_at_asc' | 'cvr_desc' | 'title_asc';
    singlePageTest: boolean;
    redirectTest: boolean;
    replaceImage: boolean;
    displayPopup: boolean;
    notActive: boolean;
    competition: boolean;
    interim: boolean;
    paused: boolean;
    archived: boolean;
    editRequest: boolean;
    variation: boolean;
    advertising: boolean;
    closed: boolean;
  }): string => {
    return [
      keyword !== '' ? `search=${keyword}` : '',
      type !== 'all' ? `type=${type}` : '',
      device !== 'all' ? `device=${device}` : '',
      testingExperience !== 'all' ? `testing_experience=${testingExperience}` : '',
      order ? `order=${order}` : 'order=created_at_desc',
      singlePageTest ? 'project_types[]=single_page_test' : '',
      redirectTest ? 'project_types[]=redirect_test' : '',
      replaceImage ? 'project_types[]=replace_image' : '',
      displayPopup ? 'project_types[]=display_popup' : '',
      notActive ? 'state[]=not_active' : '',
      competition ? 'state[]=competition' : '',
      interim ? 'state[]=interim' : '',
      paused ? 'state[]=paused' : '',
      archived ? 'state[]=archived' : '',
      editRequest ? 'notification[]=edit_request' : '',
      variation ? 'notification[]=variation' : '',
      advertising ? 'offer_state[]=advertising' : '',
      closed ? 'offer_state[]=closed' : '',
    ]
      .filter(Boolean)
      .join('&');
  };

  useEffect(() => {
    // browser-back, browser-forward
    window.addEventListener('popstate', () => {
      setConditionDisplayFromUrl(location.search, {
        setKeyword,
        setType,
        setDevice,
        setTestingExperience,
        setOrder,
        setCheckboxStates,
      });
    });
    // initial-set
    setConditionDisplayFromUrl(location.search, {
      setKeyword,
      setType,
      setDevice,
      setTestingExperience,
      setOrder,
      setCheckboxStates,
    });
  }, []);

  // 検索条件変更
  useEffect(() => {
    const condition = {
      keyword,
      type,
      device,
      testingExperience,
      order,
      ...checkboxStates,
    };
    const _queryString = _setQueryString(condition);
    // queryが変更されてない場合は中断
    if (queryString === `${_queryString}&per_page=50`) {
      return;
    }
    // query変更で検索発動
    setQueryString(`${_queryString}&per_page=50`);
    setCurrentPage(1);
    if (!lastOrg.id) {
      return;
    }
    const locationSearch = getUrlByCondition(condition);
    // url-input-areaから組織ID取得
    const path = getPathByOrganizationId(lastOrg.id);
    //ブラウザのURL-input-area差し替え
    if (locationSearch === location.search.split('?')[1] ?? '') {
      return;
    }
    // 検索条件無い場合は除外
    if (queryString === null) {
      return;
    }
    // 検索条件に応じたURLをbrowserのURL入力欄にセット
    navigate(getPathWithQueryAdd(`${location.origin}${path}`, locationSearch));
  }, [keyword, type, device, testingExperience, order, checkboxStates, lastOrg]);

  return { queryString, setQueryString, currentPage, setCurrentPage };
};
